import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useCallback } from "react";

import { SHIFT_OVERTIME_THRESHOLD_TEXT } from "../ShiftCircleProgress/utils/getOvertimeInfo";

interface AutoClockOutWarningBottomSheetProps {
  modalState: UseModalState;
  clockOutModalState: UseModalState;
  shiftId?: string;
}

export function AutoClockOutWarningBottomSheet(props: AutoClockOutWarningBottomSheetProps) {
  const { modalState, clockOutModalState, shiftId } = props;

  useLogEffect(
    APP_V2_USER_EVENTS.TIMEKEEPING_AUTO_CLOCK_OUT_WARNING_SHOWN,
    { shiftId },
    { enabled: modalState.modalIsOpen && isDefined(shiftId) }
  );

  function handleClockOut() {
    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_AUTO_CLOCK_OUT_WARNING_CLOCK_OUT_TAPPED, { shiftId });

    clockOutModalState.openModal();
    modalState.closeModal();
  }

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            size="large"
            onClick={() => {
              handleClockOut();
            }}
          >
            Clock out
          </Button>
        </DialogFooter>
      }
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <BottomSheetIllustrationContent
        hasFooter
        title="You may be auto clocked out"
        description={
          <Stack gap={6}>
            <Text semibold variant="body1" color={(theme) => theme.palette.text.secondary}>
              You&apos;re working {SHIFT_OVERTIME_THRESHOLD_TEXT} past the scheduled shift end time.
            </Text>
            <Text semibold variant="body1" color={(theme) => theme.palette.text.secondary}>
              If you need to continue working, ask your workplace to adjust the shift time in the
              app to avoid Non-InstantPay.
            </Text>
          </Stack>
        }
        illustrationType="hours-limit"
      />
    </BottomSheet>
  );
}
